import React, { useEffect, useRef, useState } from "react";
import MapView from "@arcgis/core/views/MapView";
import Map from "@arcgis/core/Map";
import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Basemap from "@arcgis/core/Basemap";
import { duvalCityCoords } from "../../data/realEstateData/cityCoordsMapping";
import { useDispatch, useSelector } from "react-redux";
import {
  setNoAddressError,
  // setAddressMapClearing,
  setSelectedRealEstateAddressTemp,
} from "../../store/actions/uiActions/myPermitsActions";
import {
  getParcelLayerCache,
  setParcelLayerCache,
  clearParcelLayerCache,
} from "../../utils/parcelLayerCache";

const createFreshParcelLayer = () =>
  new FeatureLayer({
    url: "https://maps.coj.net/coj/rest/services/CityBiz/Parcels/MapServer/0",
    outFields: ["*"],
    popupEnabled: true,
    labelingInfo: [
      {
        labelExpressionInfo: {
          expression: `Text($feature.STREET_NO) + TextFormatting.NewLine + 
            Text($feature.ST_NAME) + ' ' + Text($feature.ST_TYPE)`,
        },
        symbol: {
          type: "text",
          color: "white",
          haloColor: "black",
          haloSize: 1.5,
          font: {
            size: 12,
            family: "Arial",
            weight: "bold",
          },
        },
        labelPlacement: "always-horizontal",
      },
    ],
  });

const retryLoad = async (createLayerFn, map, maxAttempts = 5, delay = 1500) => {
  const parcelLayerCache = getParcelLayerCache();

  if (parcelLayerCache?.loaded) {
    if (!map.layers.includes(parcelLayerCache)) {
      map.add(parcelLayerCache);
    }
    return parcelLayerCache;
  }

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const layer = createLayerFn();
    map.add(layer);

    try {
      await layer.load();
      setParcelLayerCache(layer);
      return layer;
    } catch (err) {
      map.remove(layer); // important to remove broken layer
      clearParcelLayerCache();
      // console.warn(`⚠️ Attempt ${attempt} failed, err`);
      if (attempt === maxAttempts) throw err;
      await new Promise((res) => setTimeout(res, delay));
    }
  }
};

const ESRIMap = () => {
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);
  const {
    selectedAddress,
    selectedAddressTemp,
    addressMapClearing,
    selectedAddressTempObj,
  } = useSelector((state) => state.myPermitsComponent.realEstate);

  const { mapType } = useSelector((state) => state.myPermitsComponent.duvalMap);
  const dispatch = useDispatch();

  const county = selectedRow?.county;
  const city = selectedRow?.city;

  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const [isLoadingAddress, setIsLoadingAddress] = useState(false);

  useEffect(() => {
    if (!viewRef.current || !mapInstanceRef.current) return;
    setIsLoadingAddress(true);

    Basemap.fromId(mapType)
      .load()
      .then((loadedBasemap) => {
        mapInstanceRef.current.basemap = loadedBasemap;
        setTimeout(() => {
          setIsLoadingAddress(false);
        }, 1000);
      })
      .catch((err) => {
        console.error("Basemap switch failed:", err);
        setIsLoadingAddress(false);
      });
  }, [mapType]);

  useEffect(() => {
    if (!county || county.toLowerCase() !== "duval") return;
    if (!mapType || typeof mapType !== "string") return;

    setIsLoadingAddress(true);

    const {
      address_number: street_no,
      street_name,
      street_type,
    } = (selectedRow?.noc_created
      ? selectedRow?.realEstatePresets?.selectedAddress
      : !selectedAddressTemp
        ? selectedAddress
        : selectedAddressTempObj) || {};

    const currCity = city || "duval";
    const centerCoords = duvalCityCoords?.[currCity]?.coords || [
      -81.6557, 30.3322,
    ];

    const map = new Map();
    mapInstanceRef.current = map;

    const view = new MapView({
      container: mapRef.current,
      map,
      center: centerCoords,
      zoom:
        mapType === "hybrid"
          ? duvalCityCoords?.[currCity]?.hybridZoom
          : duvalCityCoords?.[currCity]?.normalZoom,
      ui: {
        components: [], // disables all default UI widgets including zoom
      },
    });

    viewRef.current = view;

    let initialGraphic = null;
    let highlightedGraphic = null;

    Basemap.fromId(mapType)
      .load()
      .then((loadedBasemap) => {
        map.basemap = loadedBasemap;

        // const view = new MapView({
        //   container: mapRef.current,
        //   map,
        //   center: centerCoords,
        //   zoom:
        //     mapType === "hybrid"
        //       ? duvalCityCoords?.[currCity]?.hybridZoom
        //       : duvalCityCoords?.[currCity]?.normalZoom,
        //   ui: {
        //     components: [], // disables all default UI widgets including zoom
        //   },
        // });

        viewRef.current = view;
        let parcelLayer = null;

        view.on("click", async (event) => {
          try {
            if (!parcelLayer || parcelLayer.destroyed) {
              // console.warn("Parcel layer is not available or was destroyed.");
              return;
            }
            if (selectedRow?.realEstatePresets?.realEstateSet) {
              return;
            }
            setIsLoadingAddress(true);

            const response = await parcelLayer.queryFeatures({
              geometry: event.mapPoint,
              spatialRelationship: "intersects",
              outFields: ["*"],
              returnGeometry: true,
            });

            if (response?.features?.length) {
              const feature = response.features[0];
              const attrs = feature.attributes;

              const city = attrs.ADDRCITY?.trim();
              const zip = attrs.ZIPCODE?.toString().trim();
              const unit = attrs.UNIT_NO?.trim();
              const streetType = attrs.ST_TYPE?.trim();

              const streetNo = attrs.STREET_NO?.trim();
              const streetDir = attrs.ST_DIR?.trim();
              const streetName = attrs.ST_NAME?.trim();

              const street = [streetDir, streetName, streetType]
                .filter(Boolean)
                .join(" ");
              const fullStreet = [streetNo, street].filter(Boolean).join(" ");

              const fullAddress = `${fullStreet}${
                unit ? " #" + unit : ""
              }, ${city}, ${zip}`.trim();
              const shortAddress = [streetNo, streetDir, streetName]
                .filter(Boolean)
                .join(" ");

              if (highlightedGraphic) {
                view.graphics.remove(highlightedGraphic);
              }
              if (initialGraphic) {
                view.graphics.remove(initialGraphic);
                initialGraphic = null;
              }

              const highlight = new Graphic({
                geometry: feature.geometry,
                symbol: {
                  type: "simple-fill",
                  color: [0, 73, 118, 0.25],
                  outline: {
                    color: [0, 73, 118, 1],
                    width: 2.5,
                  },
                },
              });

              highlightedGraphic = highlight;
              view.graphics.add(highlight);

              window.__suppressRealEstateFetch = true;
              dispatch(
                setSelectedRealEstateAddressTemp({
                  fullAddress,
                  shortAddress,
                  selectedAddressTempObj: {
                    parcel_number: attrs.PARCELNUM?.trim() || "",
                    address_number: attrs.STREET_NO?.trim() || "",
                    prefix: attrs.ST_DIR?.trim() || "",
                    street_name: attrs.ST_NAME?.trim() || "",
                    street_type: attrs.ST_TYPE?.trim() || "",
                    unit: attrs.UNIT_NO?.trim() || "",
                    city: attrs.ADDRCITY?.trim() || "",
                    zip_code: attrs.ZIPCODE?.toString().trim() || "",
                    building_number: attrs.BLDG_NUM?.toString().trim() || "",
                  },
                })
              );
            }
          } catch (err) {
            console.error("Click query failed:", err);
          } finally {
            setIsLoadingAddress(false);
          }
        });

        (async () => {
          try {
            await view.when(); // wait for view to initialize

            parcelLayer = await retryLoad(createFreshParcelLayer, map);
            if (!parcelLayer.loaded) {
              // console.warn("⚠️ ParcelLayer is still not loaded after retry.");
              return;
            }

            const whereClause =
              street_no && street_name && street_type
                ? `STREET_NO = '${street_no}' AND ST_NAME = '${street_name}' AND ST_TYPE = '${street_type}'`
                : null;

            if (!whereClause) {
              if (!selectedAddress) return;
              dispatch(setNoAddressError(true));
              return;
            }

            if (parcelLayer?.destroyed) {
              // console.warn("Parcel layer is destroyed. Skipping query.");
              return;
            }

            const result = await parcelLayer.queryFeatures({
              where: whereClause,
              outFields: ["*"],
              returnGeometry: true,
            });

            if (result?.features?.length > 0) {
              const feature = result.features[0];
              view.goTo(feature.geometry.extent.expand(1.5));

              const polygonGraphic = new Graphic({
                geometry: feature.geometry,
                symbol: {
                  type: "simple-fill",
                  color: [0, 73, 118, 0.25],
                  outline: {
                    color: [0, 73, 118, 1],
                    width: 2.5,
                  },
                },
              });

              view.graphics.removeAll();
              view.graphics.add(polygonGraphic);
              initialGraphic = polygonGraphic;
            } else {
              if (!selectedAddress) return;
              dispatch(setNoAddressError(true));
            }
          } catch (err) {
            // console.warn("❌ Fatal error in view init:", err);
          } finally {
            setTimeout(() => {
              setIsLoadingAddress(false);
            }, 1500);
          }
        })();
      })
      .catch((err) => {
        console.error("❌ Basemap load failed:", err);
      });

    return () => {
      if (viewRef.current) {
        viewRef.current.container = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    city,
    county,
    // selectedRow?.noc_created,
    // selectedRow?.project_address,
    // selectedRow?.realEstatePresets?.realEstateSet,
    // selectedRow?.realEstatePresets?.selectedAddress,
    selectedAddress,
    dispatch,
    addressMapClearing,
  ]);

  return (
    <>
      {/* <div ref={mapRef} style={{ width: "100%", height: "100%" }} /> */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
        {isLoadingAddress && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.4)",
              zIndex: 999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "none", // so clicks still go through if needed
            }}
          >
            <div
              style={{
                backgroundColor: "#333",
                padding: "20px 30px",
                borderRadius: "10px",
                color: "#fff",
                fontWeight: "bold",
                boxShadow: "0 0 15px rgba(0,0,0,0.5)",
              }}
            >
              Loading...
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(ESRIMap);
